// ============================================================
//  PROVINCE TAX RATES PAGE — ADMIN DASHBOARD
//  Path: src/pages/TaxRatesPage.tsx
//
//  Read-only. Shows the sales-tax rate used for each province and what the
//  permanent tax ledger holds for it so far. The rates are set in the backend
//  (shared/utils/taxJurisdiction.util.js) and change only through a reviewed
//  deploy, so a rate change is always deliberate and never rewrites an old
//  booking (every booking stores the rate it used).
//
//  Access: super_admin, admin only.
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getTaxProvinceRates } from '../services/admin.service'
import { TaxProvinceRate } from '../types'

const money = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const TaxRatesPage: React.FC = () => {
  const [rates, setRates] = useState<TaxProvinceRate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let superseded = false
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await getTaxProvinceRates()
        if (!superseded) setRates(res.data.data)
      } catch (err: any) {
        if (superseded) return
        console.error('Failed to load province tax rates:', err?.response?.status, err?.message)
        setError(err?.response?.data?.message || (err?.isNetworkError
          ? 'Could not reach the server — check your connection and try again.'
          : 'Failed to load the province rates.'))
      } finally {
        if (!superseded) setLoading(false)
      }
    }
    load()
    return () => { superseded = true }
  }, [reloadKey])

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Province Tax Rates</h1>
          <p className="text-gray-400 text-sm mt-1">
            The rate charged on a booking depends on the province where the service is supplied. Rates are fixed in the
            backend and change only through a reviewed release. Every booking keeps the rate it used.
          </p>
        </div>
        <Link to="/tax-report" className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors shrink-0">
          Tax report
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={() => setReloadKey((k) => k + 1)}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">Try again</button>
        </div>
      ) : (
        <div className="bg-surface-card border border-border-light rounded-2xl overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-border-light">
                {['Province', 'Code', 'Tax', 'Rate', 'Job-price rows', 'Job price (net)', 'Tax on job price'].map((h, i) => (
                  <th key={h} className={`px-5 py-3 text-xs font-semibold text-gray-400 uppercase ${i >= 3 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.code} className="border-b border-border-light last:border-0">
                  <td className="px-5 py-3 text-sm text-white">{r.name}</td>
                  <td className="px-5 py-3 text-sm text-gray-300">{r.code}</td>
                  <td className="px-5 py-3 text-sm text-gray-300">{r.label}</td>
                  <td className="px-5 py-3 text-sm text-white text-right font-medium">{r.ratePercent}%</td>
                  <td className="px-5 py-3 text-sm text-gray-400 text-right">{r.jobPriceRows}</td>
                  <td className="px-5 py-3 text-sm text-gray-300 text-right">{money(r.jobPriceBase)}</td>
                  <td className="px-5 py-3 text-sm text-gray-300 text-right">{money(r.jobPriceTax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-5 py-3 text-xs text-gray-500 border-t border-border-light">
            Job-price figures come from the permanent tax ledger, net of refunds. They cover bookings of taskers registered for GST/HST
            (others have no tax on the job price). Quebec shows GST 5% + QST 9.975% combined. A job with no known province uses the platform default rate.
          </p>
        </div>
      )}
    </div>
  )
}

export default TaxRatesPage